import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getNonDraftedOffers } from "../../../services/offerService";
import { ReactComponent as MapIcon } from "../../../Assets/Icons/MapIcon.svg";
import { ReactComponent as MailIcon } from "../../../Assets/Icons/mailIcon.svg";
import { ReactComponent as WebIcon } from "../../../Assets/Icons/webIcon.svg";
import { ReactComponent as UserIcon } from "../../../Assets/Icons/userIcon.svg";
import { ReactComponent as PhoneIcon } from "../../../Assets/Icons/phoneIcon.svg";
import "../../common/OfferList/offerList.scss";
import defaultCategoryIcon from "../../../Assets/Icons/subCategoryIcon.svg";
import Confirmation from "../../common/OfferList/Confirmation";
import StoresTable from "./StoresTable";
import { Dropdown, Tooltip } from "antd";
import eyeIcon from "../../../Assets/Icons/eyeviewicon.svg";
import { ReactComponent as DeleteIcon } from "../../../Assets/Icons/deleteIcon.svg";
import { getS3PublicUrl } from "../../../utils/s3";



const PopupContent = ({ merchantDetails, onDelete, onApprove, onDecline, onReviewStatus, onShowReviewModal, onChangeLogo, onEdit, onStatusChangeRequest, onAddLocation,
  onUploadCSV,
  onEditLocation
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useSelector((state) => state?.authReducer?.tokenData?.user?.role);
  const [offerCount, setOfferCount] = React.useState(merchantDetails?.offersCount || 0);
  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);

  const formatStatusLabel = (val) => {
    if (!val) return "";
    const s = String(val).trim().replace(/_/g, " ").replace(/-/g, " ").toLowerCase();
    return s.replace(/\b\w/g, (c) => c.toUpperCase());
  };

  React.useEffect(() => {
    const fetchOfferCount = async () => {
      if (!merchantDetails?._id) return;
      try {
        const response = await dispatch(getNonDraftedOffers({
          merchantId: merchantDetails._id,
          status: 'approved',
          limit: 1,
          silent: true
        })).unwrap();
        if (response?.success) {
          const count = response?.total || response?.totalCount || response?.count || (Array.isArray(response?.data) ? response?.data?.length : 0);
          setOfferCount(count);
        }
      } catch (error) {
        console.error("Error fetching offer count:", error);
      }
    };

    if (merchantDetails?._id && !merchantDetails?.offersCount) {
      fetchOfferCount();
    } else if (merchantDetails?.offersCount) {
      setOfferCount(merchantDetails.offersCount);
    }
  }, [merchantDetails?._id, merchantDetails?.offersCount, dispatch]);

  const {
    address,
    businessName,
    category,
    contactPersonName,
    contactPersonNumber,
    logo,
    user,
    website,
    _id,
    status,
    description,
    stores,
    // Fallback fields for in-process merchants
    email,
    phoneNumber,
    phone,
    contact,
    mobile,
    location,
    businessAddress,
    business_address,
    websiteUrl,
    url,
    contactName,
    name,
    aboutUs,
    aboutBusiness
  } = merchantDetails || {}; // Safety check

  const statusLower = String(status || "").toLowerCase();
  const isDeletedMerchant = statusLower === "deleted";
  const isDeletableMerchant = ["approved", "live", "active", "disabled", "declined", "rejected"].includes(statusLower);

  // Derived values to handle both data structures
  const displayEmail = user?.email || email || "N/A";
  const displayContact = contactPersonNumber || phoneNumber || phone || contact || mobile || "N/A";
  const displayAddress = address || location || businessAddress || business_address || "N/A";
  const displayWebsite = website || websiteUrl || url || "N/A";
  const displayContactName = contactPersonName || contactName || name || user?.name || "N/A";

  // Display all assigned stores exactly as returned by the API
  const displayStores = stores || [];

  const sortedStores = [...displayStores].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const wrapStatusWithTooltip = (node, label) => (
    <Tooltip
      title={label}
      placement="top"
      getPopupContainer={() => document.body}
      overlayStyle={{ zIndex: 10050 }}
    >
      <span className="inline-flex">{node}</span>
    </Tooltip>
  );

  if (!merchantDetails) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="loader"></div>
        <p className="mt-4 text-[#767676] font-poppins text-[15px]">Loading merchant details...</p>
      </div>
    );
  }

  return (
    <div
      style={{ scrollbarWidth: "none" }}
      className="max-h-[85vh] overflow-y-auto w-full md:w-auto min-w-[300px] flex flex-col gap-4 font-poppins scrollbar-hidden pb-12"
    >
      <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 sm:gap-6">
        <div className="flex justify-start items-center gap-4 flex-1 min-w-0 w-full sm:w-auto">
          <div className="relative flex-shrink-0">
            {(function () {
              const l = logo;
              const isMissing = !l || (typeof l === 'string' && l.trim() === '');
              const src = (function () {
                if (isMissing) return require("../../../Assets/Images/PersonBlue.svg").default;
                const baseUrl = getS3PublicUrl(l);
                // Append timestamp to bust cache when logo is updated
                return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
              })();
              if (isMissing) {
                return (
                  <>
                    <div className="w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-full bg-[#E6F7FF] border border-[#00ADEF33] flex items-center justify-center flex-shrink-0">
                      <img src={src} alt="merchantLogo" className="w-[38px] h-[38px] md:w-[44px] md:h-[44px] object-contain" />
                    </div>
                    {(role === "admin" || role === "ADMIN") && onChangeLogo && !isDeletedMerchant && (
                      <button
                        onClick={() => onChangeLogo(merchantDetails)}
                        className="absolute bottom-0 right-0 w-[20px] h-[20px] md:w-[24px] md:h-[24px] rounded-full bg-[#00ADEF] flex items-center justify-center border-2 border-white shadow-md hover:bg-[#0088CC] transition-colors"
                        title="Change Logo"
                      >
                        <img
                          src={require("../../../Assets/Icons/editIcon.svg").default}
                          alt="Edit"
                          className="w-[10px] h-[10px] md:w-[12px] md:h-[12px] filter brightness-0 invert"
                        />
                      </button>
                    )}
                  </>
                );
              }
              return (
                <>
                  <div
                    className="w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-full bg-white border border-[#00ADEF33] flex items-center justify-center flex-shrink-0 overflow-hidden"
                  >
                    <img
                      src={src}
                      alt="merchantLogo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {(role === "admin" || role === "ADMIN") && onChangeLogo && !isDeletedMerchant && (
                    <button
                      onClick={() => onChangeLogo(merchantDetails)}
                      className="absolute bottom-0 right-0 w-[20px] h-[20px] md:w-[24px] md:h-[24px] rounded-full bg-[#00ADEF] flex items-center justify-center border-2 border-white shadow-md hover:bg-[#0088CC] transition-colors"
                      title="Change Logo"
                    >
                      <img
                        src={require("../../../Assets/Icons/editIcon.svg").default}
                        alt="Edit"
                        className="w-[10px] h-[10px] md:w-[12px] md:h-[12px] filter brightness-0 invert"
                      />
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          <div className="flex items-center gap-2 max-w-full">
            <h1 className="font-[600] text-[16px] md:text-[18px] lg:text-[20px] truncate max-w-[200px] sm:max-w-[250px] md:max-w-[350px]">
              {businessName}
            </h1>
            {typeof onDelete === "function" && isDeletableMerchant && !isDeletedMerchant && (
              <Tooltip
                title="Delete Merchant"
                color="#00ADEF"
                placement="top"
                getPopupContainer={() => document.body}
                overlayStyle={{ zIndex: 100000 }}
              >
                <button
                  type="button"
                  onClick={() => onDelete(_id)}
                  className="p-1.5 ml-1 rounded-full hover:bg-[#fff1f0] transition-colors focus:outline-none flex items-center justify-center group"
                >
                  <DeleteIcon className="w-4 h-4 text-[#ff4d4f] group-hover:scale-110 transition-transform" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 flex-shrink-0 mt-2 sm:mt-0 sm:ml-auto">
          {/* Status Badge at the top right */}
          <div className="flex items-center gap-1">
            {(role === "admin" || role === "ADMIN") && (
              ["approved", "live", "declined", "disabled", "rejected"].includes(
                String(status || "").toLowerCase()
              )
            ) ? (
              <Tooltip
                title={statusDropdownOpen ? null : formatStatusLabel(status)}
                open={statusDropdownOpen ? false : undefined}
                mouseEnterDelay={0.08}
                mouseLeaveDelay={0}
                placement="top"
                getPopupContainer={() => document.body}
                overlayStyle={{ zIndex: 10050 }}
              >
                <span className="inline-flex">
              <Dropdown
                menu={{
                  items: [
                    {
                      key: '1',
                      label: (
                        <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                          <div
                            className="flex items-center justify-center w-[16px] h-[16px] rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: (status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live') ? '#EC826E' : '#50CD89'
                            }}
                          >
                            {(status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live') ? (
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ display: 'block' }}>
                                <path d="M6 2L2 6M2 2L6 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                            ) : (
                              <svg width="8" height="6" viewBox="0 0 8 6" fill="none" style={{ display: 'block' }}>
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <span className="text-[13px] font-[400] font-poppins text-[#303033]">
                            {(status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live') ? 'Disable' : 'Live'}
                          </span>
                        </div>
                      ),
                      onClick: () =>
                        onStatusChangeRequest &&
                        onStatusChangeRequest(
                          (status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live')
                            ? 'declined'
                            : 'approved'
                        )
                    }
                  ]
                }}
                trigger={['click']}
                placement="bottomLeft"
                getPopupContainer={(trigger) => trigger?.parentNode || document.body}
                overlayStyle={{ zIndex: 100000 }}
                open={statusDropdownOpen}
                onOpenChange={(nextOpen) => setStatusDropdownOpen(nextOpen)}
              >
                <div
                  className="cursor-pointer flex items-center justify-start h-[26px] pl-2.5 pr-2 rounded-full border-[0.5px] transition-all hover:opacity-90 select-none"
                  style={{
                    backgroundColor: (status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live') ? 'rgba(80, 205, 137, 0.15)' : 'rgba(236, 130, 110, 0.15)',
                    borderColor: (status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live') ? '#50CD89' : '#EC826E',
                    width: '116px'
                  }}
                >
                  <div
                    className="flex items-center justify-center w-[16px] h-[16px] rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: (status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live') ? '#50CD89' : '#EC826E'
                    }}
                  >
                    {(status?.toLowerCase() === 'approved' || status?.toLowerCase() === 'live') ? (
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none" style={{ display: 'block' }}>
                        <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" style={{ display: 'block' }}>
                        <path d="M7 12h10" />
                      </svg>
                    )}
                  </div>

                  <span className="text-[12px] font-[400] ml-2 font-poppins text-[#303033] truncate flex-1 text-left">
                    {formatStatusLabel(status)}
                  </span>

                  <div className="ml-2 flex items-center flex-shrink-0">
                    <svg width="7" height="4" viewBox="0 0 10 6" fill="none">
                      <path d="M1 1.5L5 5.5L9 1.5" stroke="#303033" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </Dropdown>
                </span>
              </Tooltip>
            ) : ["pending", "accepted", "completed", "in-process", "in-progress", "in_progress"].includes(String(status || "").toLowerCase()) ? (
              wrapStatusWithTooltip(
                <div
                  className="flex items-center justify-center gap-2 h-[26px] px-2.5 rounded-full border-[0.5px] select-none"
                  style={{
                    backgroundColor: "#FFF1D6",
                    borderColor: "#FFC961",
                    width: "110px",
                  }}
                >
                  <svg width="11" height="12" viewBox="0 0 11 12" fill="none" style={{ display: "block" }}>
                    <path d="M10.2083 1.16667H9.33333V1.72322C9.33333 2.55097 8.97616 3.33541 8.35981 3.87369L8.23334 3.97738L6.02875 5.6875L8.23334 7.3976L8.35981 7.50132C8.97616 8.03962 9.33333 8.82403 9.33333 9.65177V10.2083H10.2083V11.375H0V10.2083H0.875V9.65177C0.875 8.76902 1.28139 7.93596 1.97502 7.3976L4.17906 5.6875L1.97502 3.97738C1.28139 3.43906 0.875 2.60598 0.875 1.72322V1.16667H0V0H10.2083V1.16667ZM2.00293 1.16667V1.72322C2.00293 2.25296 2.24688 2.75319 2.66317 3.07617L5.10417 4.96971L7.54518 3.07617L7.62148 3.01294C7.99114 2.68998 8.2054 2.21972 8.2054 1.72322V1.16667H2.00293Z" fill="#FFC961"/>
                  </svg>
                  <p className="text-[12px] font-[400] font-poppins text-[#303033] whitespace-nowrap">In-Progress</p>
                </div>,
                "In-Progress"
              )
            ) : isDeletedMerchant ? (
              wrapStatusWithTooltip(
                <div
                  className="flex items-center justify-start gap-2 h-[26px] px-2.5 rounded-full border-[0.5px] select-none"
                  style={{
                    backgroundColor: "rgba(236, 130, 110, 0.15)",
                    borderColor: "#EC826E",
                    width: "110px",
                  }}
                >
                  <div
                    className="flex items-center justify-center w-[16px] h-[16px] rounded-full flex-shrink-0"
                    style={{ backgroundColor: "#EC826E" }}
                  >
                    <svg width="12" height="12" viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
                      <path d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z" fill="white" />
                    </svg>
                  </div>
                  <span className="text-[12px] font-[400] font-poppins text-[#303033] truncate flex-1 text-left">
                    Deleted
                  </span>
                </div>,
                "Deleted"
              )
            ) : (
              <Confirmation status={status} />
            )}
          </div>

          {/* Edit Profile Button below the Status Badge */}
          <div className="flex items-center gap-2">
            {(role === "admin" || role === "ADMIN") && onEdit && (() => {
              if (isDeletedMerchant) return null;
              const s = String(status || "").toLowerCase().trim();
              const normalized = s.replace(/\s+/g, "-").replace(/_/g, "-");
              const isInProgress = ["pending", "in-process", "in-progress", "in-progress"].includes(normalized);
              if (isInProgress) return null;
              return (
                <button
                  type="button"
                  onClick={() => onEdit(merchantDetails)}
                  className="px-6 py-2 text-[13px] font-[600] bg-[#00ADEF] text-white rounded-[6px] hover:bg-[#0088CC] transition-colors whitespace-nowrap shadow-sm"
                >
                  Edit Profile
                </button>
              );
            })()}
          </div>
        </div>

      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {Array.isArray(category) && category.length > 0 && category.map((c, index) => {
          const title = typeof c === 'string' ? c : (c?.title || '');

          // Check if title looks like a Mongo ID (24 hex chars)
          // If it is, we skip rendering it because the user doesn't want to see IDs
          if (/^[0-9a-fA-F]{24}$/.test(title)) {
            return null;
          }

          const imgPath = typeof c === 'string' ? (c?.image || '') : (c?.image || '');
          const src = (function () {
            const url = imgPath;
            const isAbsolute = typeof url === 'string' && /^(https?:)?\/\//i.test(url);
            if (isAbsolute && url) return url;
            const baseUrl = process.env.REACT_APP_ICON_URL || '';
            if (url) {
              // ensure exactly one slash between base and path
              const needsSlash = baseUrl && !baseUrl.endsWith('/') && url && !url.startsWith('/');
              return `${baseUrl}${needsSlash ? '/' : ''}${url}`;
            }
            // fallback to local default icon
            return defaultCategoryIcon;
          })();
          return (
            <div key={`merchant-cat-${index}-${title}`} className="flex justify-start items-center gap-3">
              <img
                src={src}
                alt="categoryImg"
                className="w-[22px] h-[22px] filter-blue"
              />
              <h3 className="text-[#303030] text-[14px]">{title}</h3>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">
        <div className="flex gap-2 items-center">
          <MailIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayEmail}</p>
        </div>
        <div className="flex gap-2 items-center">
          <WebIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayWebsite}</p>
        </div>
        <div className="flex gap-2 items-center">
          <UserIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayContactName}</p>
        </div>
        <div className="flex gap-2 items-center">
          <PhoneIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayContact}</p>
        </div>
      </div>
      <div className="flex mt-1 justify-start items-center gap-2 text-[14px] font-[400] text-[#767676]">
        <MapIcon />
        <p>{displayAddress}</p>
      </div>



      <div className="w-full flex flex-col mt-2.5 gap-1 items-start font-poppins">
        <h2 className="text-[15px] font-[600] text-[#000000] text-start">
          Description:
        </h2>
        <p className="w-full text-start text-[13px] font-[400] text-[#767676] leading-relaxed">
          {aboutUs || description || aboutBusiness || "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."}
        </p>
      </div>

      {/* Offer Count Section */}
      <div className="flex flex-col mt-2 w-full items-start">
          <div className="flex items-center gap-1.5">
          <h2 className="text-[15px] font-[600] text-[#303030] font-poppins text-start">
            Offer Count ({offerCount})
          </h2>
          {!isDeletedMerchant && (
            <Tooltip
              title="View offers"
              color="#00ADEF"
              placement="top"
              getPopupContainer={() => document.body}
              overlayStyle={{ zIndex: 100000 }}
            >
              <button
                type="button"
                className="flex items-center justify-center border-none bg-transparent hover:opacity-80 transition-opacity -mt-[2px]"
                onClick={() => {
                  const normalizedRole = String(role || "").toLowerCase();
                  const offerListBasePath =
                    normalizedRole === "admin"
                      ? "/admin/offer-list"
                      : normalizedRole === "merchant"
                        ? "/merchant/offer-list"
                        : "/offer-list";
                  navigate(
                    `${offerListBasePath}?search=${encodeURIComponent(
                      businessName
                    )}&merchantId=${merchantDetails?._id}`
                  );
                }}
                title="View offers"
              >
                <img
                  src={eyeIcon}
                  alt="View offers"
                  className="w-[20px] h-[20px] cursor-pointer"
                  style={{ filter: "invert(50%) sepia(85%) saturate(1800%) hue-rotate(175deg) brightness(100%) contrast(100%)" }}
                />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Merchant Store Locations Card Section */}
      <div className="flex flex-col mt-1 w-full items-start">
        <h2 className="text-[15px] font-[600] text-[#303030] mb-2 font-poppins text-start">
          Merchant store locations ({displayStores.length}):
        </h2>

        {/* Scrollable Locations Container */}
        <div
          className="bg-white rounded-[10px] p-4 flex flex-col gap-3 overflow-y-auto scrollbar-hidden w-[98%] mx-auto"
          style={{
            maxHeight: '240px',
            boxShadow: '0px 4px 30px 0px rgba(121, 121, 121, 0.15)'
          }}
        >
          {sortedStores.length > 0 ? (
            sortedStores.map((store, index) => (
              <div
                key={index}
                className="flex justify-between items-center py-2.5 px-4 bg-white border border-[#E0E0E0] rounded-[8px] hover:border-[#00ADEF66] transition-all shadow-sm group"
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <span className="text-[13px] text-[#424242] font-poppins truncate leading-5">
                    {store.address || store.location || store.googlePlaceId?.address}
                  </span>
                </div>
                {!isDeletedMerchant && (
                  <button
                    onClick={() => onEditLocation && onEditLocation(store)}
                    className="text-[13px] text-[#00ADEF] font-[600] hover:text-[#0088CC] transition-colors ml-4 flex-shrink-0"
                  >
                    Edit
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-[#767676] text-[13px] font-poppins">No store location added yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Store Location Action Buttons */}
      {!isDeletedMerchant && (
        <div className="flex flex-col mt-6 gap-3 items-start relative z-50">
        <h3 className="text-[14px] font-[600] text-[#303030] font-poppins">Add Store Location</h3>
        <div className="flex flex-wrap gap-4 w-full">
          <button
            type="button"
            onClick={() => onAddLocation && onAddLocation()}
            className="flex items-center gap-2.5 bg-[#00ADEF] text-white px-6 py-3 rounded-[8px] text-[13px] font-[700] hover:bg-[#0088CC] transition-all shadow-md active:scale-[0.95] cursor-pointer"
          >
            <MapIcon className="w-[18px] h-[18px] filter-white" />
            Add a New Store Location
          </button>

          <button
            type="button"
            onClick={() => onUploadCSV && onUploadCSV()}
            className="flex items-center gap-2.5 bg-[#00ADEF] text-white px-6 py-3 rounded-[8px] text-[13px] font-[700] hover:bg-[#0088CC] transition-all shadow-md active:scale-[0.95] cursor-pointer"
          >
            <img src={require("../../../Assets/Icons/csvIcon.svg").default} alt="CSV" className="w-[18px] h-[18px] filter-white" />
            Upload Via CSV
          </button>
        </div>
      </div>
      )}

      {/* Merchants are auto-approved; no Approve/Decline buttons in modal */}


    </div>
  );
};

export default PopupContent;
